import React, { useState } from 'react';
import { Box, TextField, Button, Typography, MenuItem } from '@mui/material';
import { auth } from '../Firebase';
import { db } from '../Firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import SofBenLogo from '../Img/SofBenLogo.jpg';

function Login() {

  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('CA');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');


  // Possible Errors
  const getFriendlyError = (code) => {
    switch (code) {
      case 'auth/invalid-email':
        return "Email invalide.";
      case 'auth/user-not-found':
        return "Aucun compte trouvé avec cet email.";
      case 'auth/wrong-password':
        return "Mot de passe incorrect.";
      case 'auth/email-already-in-use':
        return "Cet email est déjà utilisé.";
      case 'auth/weak-password':
        return "Le mot de passe est trop faible (min. 6 caractères).";
      case 'auth/invalid-credential':
        return "Adresse courriel ou mot de passe incorrect";
      default:
        return "Une erreur s’est produite. Veuillez réessayer.";
    }
  };

  
  const toE164 = (country, nationalDigits) => {
    const digits = (nationalDigits || "").replace(/\D/g, "");

    if (country === "CA") {
      // Canada NANP: 10 digits (optionally user may type leading 1)
      const normalized = digits.startsWith("1") && digits.length === 11 ? digits.slice(1) : digits;
      return normalized.length === 10 ? `+1${normalized}` : null;
    }

    if (country === "UK") {
      // UK: users often type 07..., remove leading 0 then prefix +44
      const normalized = digits.startsWith("0") ? digits.slice(1) : digits;
      // Allow common lengths after normalization
      return normalized.length >= 9 && normalized.length <= 12 ? `+44${normalized}` : null;
    }

    return null;
  };

  const isValidE164 = (e164) => /^\+[1-9]\d{7,14}$/.test(e164);


  // LOGIN && REGISTER function
  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);

    if (!email || !password || (isRegistering && (!fullName || !phone || !confirmPassword))) {
      setErrorMsg("Veuillez remplir tous les champs.");
      setLoading(false);
      return;
    }
    
    if (isRegistering) {
      if (password !== confirmPassword) {
        setErrorMsg("Les mots de passe ne correspondent pas");
        setLoading(false);
        return;
      }

      const phoneE164 = toE164(phoneCountry, phone);
      if (!phoneE164 || !isValidE164(phoneE164)) {
        setErrorMsg("Numéro de téléphone invalide. Vérifiez le pays et le numéro.");
        setLoading(false);
        return;
      }
      try {

        // Register Save User data into the collection
        const userCred = await createUserWithEmailAndPassword(auth, email, password);
        const uid = userCred.user.uid;

        await setDoc(doc(db, "users", uid), {
          fullName,
          phone: phone.replace(/\D/g, ""),   
          phoneCountry,
          phoneE164,
          smsOptIn: true,
          email,
          role: "worker",
          date: new Date().toISOString(),
          isApproved: false,
          isClockedIn: false,
        });
        

        // Update AdminNotifications doc (create if not exist)
        const notifRef = doc(db, 'AdminNotifications', 'main');
        const notifSnap = await getDoc(notifRef);

        if (notifSnap.exists()) {
          await updateDoc(notifRef, {
            newUsers: (notifSnap.data().newUsers || 0) + 1,
          });
        } else {
          await setDoc(notifRef, {
            newUsers: 1,
            newMessages: 0,
          });
        }

        navigate('/');
        // You can also save fullName/phone to the DB here if needed
      } catch (error) {
        setErrorMsg(getFriendlyError(error.code));
      }
    } else {
      try {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/');
        // You can navigate to another page here
      } catch (error) {
        setErrorMsg(getFriendlyError(error.code));
      }
    }

    setLoading(false);
  };
  

  // Handle forgot user password
  const handleForgotPassword = async () => {
    setErrorMsg('');
    setSuccessMsg('');

    if (!email) {
      setErrorMsg("Veuillez entrer votre adresse courriel.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccessMsg("Email envoyé ! Vérifiez votre boîte de réception (et vos SPAMS) pour réinitialiser votre mot de passe.");
    } catch (error) {
      setErrorMsg(getFriendlyError(error.code));
    }
  };



  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      {/* Placeholder logo (replace with real one) */}
      <Box
        component="img"
        src= {SofBenLogo}
        alt="logo"
        sx={{ width: 300, mb: 5 }}
      />

      <Typography variant="h5"  sx={{ color: '#040303' }}>
        Bienvenue chez SofBen !
      </Typography>
      <Typography variant="body2" sx={{ color: '#999', mb: 4 }}>
        Gardez votre historique de travail organisé avec nous
      </Typography>

      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
            width: '100%',
            maxWidth: 350,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
        }}
        >
        {isRegistering && (
            <TextField
            type="text"
            placeholder="Nom complet"
            fullWidth
            variant="outlined"
            size="small"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={{ backgroundColor: '#F8F8F8' }}
            />
        )}

        {isRegistering && (
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Pays"
            value={phoneCountry}
            onChange={(e) => setPhoneCountry(e.target.value)}
            sx={{ backgroundColor: "#F8F8F8" }}
          >
            <MenuItem value="CA">Canada (+1)</MenuItem>
            <MenuItem value="UK">Royaume-Uni (+44)</MenuItem>
          </TextField>
        )}

        {isRegistering && (
          <TextField
            type="tel"
            placeholder={phoneCountry === "CA" ? "Numéro (ex: 4389277784)" : "Numéro (ex: 7912345678)"}
            fullWidth
            variant="outlined"
            size="small"
            value={phone}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "");
              setPhone(digitsOnly);
            }}
            sx={{ backgroundColor: "#F8F8F8" }}
          />
        )}

        <TextField
            type="email"
            placeholder="Adresse courriel"
            fullWidth
            variant="outlined"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ backgroundColor: '#F8F8F8' }}
        />

        <TextField
            type={showPass ? 'text' : 'password'}
            placeholder="Mot de passe"
            fullWidth
            variant="outlined"
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ backgroundColor: '#F8F8F8' }}
        />

        {isRegistering && (
            <TextField
            type={showPass ? 'text' : 'password'}
            placeholder="Confirmez le mot de passe"
            fullWidth
            variant="outlined"
            size="small"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ backgroundColor: '#F8F8F8' }}
            />
        )}

        {errorMsg && (
          <Typography
            variant="body2"
            sx={{ color: 'red', textAlign: 'center', mt: 1 }}
          >
            {errorMsg}
          </Typography>
        )}
        {successMsg && (
          <Typography
            variant="body2"
            sx={{ color: 'green', textAlign: 'center', mt: 1 }}
          >
            {successMsg}
          </Typography>
        )}


        <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
            mt: 1,
            py: 1.5,
            fontWeight: 'bold',
            backgroundColor: '#c93d3d',
            borderRadius: 10,
            '&:hover': {
                backgroundColor: '#a83131',
            },
            }}
        >
            {loading ? 'Chargement...' : isRegistering ? "S'inscrire" : 'Se connecter'}
        </Button>
      </Box>


      
      {!isRegistering && (
        <Typography
          variant="body2"
          sx={{ color: '#caa54c', mt: 2, cursor: 'pointer', textAlign: 'center' }}
          onClick={handleForgotPassword}
        >
          Mot de passe oublié ?
        </Typography>
      )}


      <Typography
        variant="body2"
        sx={{ mt: 3 }}
      >
        {isRegistering ? 'Vous avez déjà un compte ? ' : 'Vous n’avez pas de compte ? '}
        
        <Box
        component="span"
        onClick={() => setIsRegistering(!isRegistering)}
        sx={{ color: '#caa54c', fontWeight: 'bold', cursor: 'pointer' }}
        >
          {isRegistering ? 'Se connecter!' : "S'inscrire"}
        </Box>

      </Typography>
    </Box>
  );
}

export default Login;
